import {CellStyle, ColDef, EditableCallbackParams, GridApi, NewValueParams, ValueGetterParams} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import NumberUtils from "@/utils/common/NumberUtils.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import ColumnFiltrationFactory from "@/utils/UIConstants/ColumnFiltrationFactory.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import EditableGridHandler from "@/utils/presenters/EditableGridHandler.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import AgCustomInnerHeader from "@/components/EditView/AgCustomInnerHeader.vue";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;
import DTOFieldSpecs = namaTypes.DTOFieldSpecs;
import FieldMetaData = namaTypes.FieldMetaData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;

export default abstract class AbsAGGridEditViewColumn {
  protected column: EditScreenGridColumn;
  protected handler: EditableGridHandler;
  protected enabled: boolean = true;

  protected constructor(column: EditScreenGridColumn, handler: EditableGridHandler) {
    this.column = column;
    this.handler = handler;
  }

  abstract createColDef(): ColDef[];

  get id() {
    return this.columnMetadata.fieldId ?? this.column.id!;
  }

  get trimmedFieldId() {
    return this.processTrimmedFieldId(this.fetchTrimmedId(this.id, this.handler.gridId));
  }

  get entityType() {
    return this.handler.entityType;
  }

  get columnMetadata() {
    return MetaData.findFieldMetadata(this.column.id!, this.handler.metadata);
  }

  isEnabled() {
    return this.enabled;
  }

  processTrimmedFieldId(trimmedFieldId: string) {
    return trimmedFieldId;
  }

  getValue(object: Record<string, any>) {
    return NamaCommon.getFieldValue(object, this.trimmedFieldId);
  }

  fetchTrimmedId(columnId: string, gridId: string) {
    if (columnId === gridId)
      return "";
    return columnId.substring(gridId.length + 1);
  }

  static defaultColDef(instance: AbsAGGridEditViewColumn): ColDef {
    let filterData = ColumnFiltrationFactory.fetchFilterData(instance);
    let defaultColDef: ColDef = {
      field: instance.trimmedFieldId,
      colId: instance.column.id,
      context: instance.columnMetadata,
      headerName: Translator.translateTitledId(instance.entityType, instance.column),
      valueGetter: (params: ValueGetterParams) => {
        return instance.getValue(params.data);
      },
      cellStyle: this.createCellStyle(instance),
      width: GUIUtilities.convertEmToPx(instance.column.width ?? "8em"),
      valueSetter: (event) => {
        const rows: any[] = NamaCommon.getFieldValue(instance.handler.object, instance.handler.gridId);
        const realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === event.node?.data.id);
        NamaCommon.setFieldValue(instance.handler.object, instance.id, event.newValue, realRowIndex);
        return true;
      },
      onCellValueChanged: async (event) => {
        if (event.newValue === event.oldValue) {
          const rows: any[] = NamaCommon.getFieldValue(instance.handler.object, instance.handler.gridId);
          const realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === event.node?.data.id);
          event.newValue = NamaCommon.getFieldValue(instance.handler.object, instance.id, realRowIndex);
        }
        //this is a fix for a bug in AGGrid - sometimes the newValue and oldValue are equal even though they are changed
        //and the change was correctly received in the valueSetter event above
        if (!event.oldValue && ObjectChecker.isDeepEmpty(event.newValue))
          return;
        if (event.newValue?.findFromServer) {
          const rows: any[] = NamaCommon.getFieldValue(instance.handler.object, instance.handler.gridId);
          const realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === event.node?.data.id);
          let searchContextInfo: FieldActionContextInfo = instance.handler.presenter.createSearchContextInfo(instance.id, realRowIndex);
          EditScreenFieldUtils.findRefByCode(event.newValue.entityType, event.newValue.code, searchContextInfo)
            .then(async (matchedRecord) => {
              if (!matchedRecord && event.colDef.context && (event.colDef.context as FieldMetaData).fieldType === FieldType.Genericreference)
                matchedRecord = {entityType: event.newValue.entityType}
              event.newValue = matchedRecord;
              NamaCommon.setFieldValue(instance.handler.object, instance.id, event.newValue, realRowIndex);
              await instance.onValueChanged(instance, event);
            }).catch(e => {
            console.log(e);
          });
        } else {
          await instance.onValueChanged(instance, event);
        }
      },
      singleClickEdit: true,
      maxWidth: Math.max(window.innerWidth * 0.2, 500),
      editable: (params: EditableCallbackParams): boolean => {
        const isColumnDisabled = instance.handler.presenter.isColumnDisabled(params.data.id, instance.handler.gridId, instance.id);
        const isBaseDisabledField = EditPresenter.getBaseDisabledFields().includes(instance.id);
        return !(isColumnDisabled || isBaseDisabledField);
      },
      filter: filterData?.filter,
      filterParams: filterData?.filterParams,
      headerComponentParams: {
        innerHeaderComponent: AgCustomInnerHeader,
        blockOrField: () => instance.column,
        entityType: instance.entityType,
        colId: instance.id
      },
    };
    defaultColDef.field = AGGridUtils.encodeColId(defaultColDef.field);
    defaultColDef.colId = AGGridUtils.encodeColId(defaultColDef.colId);
    return defaultColDef
  }

  async onValueChanged(instance: AbsAGGridEditViewColumn, event: NewValueParams) {
    if (event.oldValue === event.newValue || NamaCommon.deepEqual(event.newValue, event.oldValue))
      return;
    const rows: any[] = NamaCommon.getFieldValue(instance.handler.object, instance.handler.gridId);
    const realRowIndex = rows.indexOf(rows.find(l => l.id === event.node?.data.id));
    await AbsAGGridEditViewColumn.callPostActions(this.handler.dispatcher, this.id, realRowIndex, event.api)
    if (document.activeElement === document.body && this.handler.focusHandler)
      this.handler.focusHandler();
  }

  static async callPostActions(dispatcher: BaseDispatcher, colId: string, rowIndex: number, api: GridApi) {
    await useLoadingStore().awaitLoadingFinish(1);
    useLoadingStore().startLoading();
    if (dispatcher)
      await dispatcher.validateColumn(colId, rowIndex, false);
    useLoadingStore().stopLoading();
    api.refreshCells({
      force: false,
      suppressFlash: false,
    });

  }
  createSpecs(): namaTypes.DTOFieldSpecs | null | undefined {
    return {
      decimalPlaces: NumberUtils.calcFractionalDecimalPlaces(this.columnMetadata, this.handler.object),
      format: ClientCache.getFieldDisplayMask(this.entityType, this.columnMetadata.fieldId!)
    } as DTOFieldSpecs;
  }

  static createCellStyle(instance: AbsAGGridEditViewColumn) {
    let cellStyle = {} as CellStyle;
    let style = ClientCache.getFieldStyle(instance.entityType, instance.columnMetadata.fieldId!);
    if (style) {
      if (style.direction)
        cellStyle.direction = style.direction;
      else if (AbsAGGridEditViewColumn.alwaysLTRField(instance.columnMetadata))
        cellStyle.direction = "ltr";
      if (style.backColor)
        cellStyle['background-color'] = style.backColor;
      if (style.foreColor)
        cellStyle['color'] = style.foreColor;
      if (style.doNotGoToNextWithEnter)
        null;// TODO
    } else if (AbsAGGridEditViewColumn.alwaysLTRField(instance.columnMetadata)) {
      cellStyle.direction = "ltr";
    }
    return cellStyle;
  }

  static alwaysLTRField(metaData: FieldMetaData) {
    if (!metaData || !metaData.fieldType)
      return false;
    switch (metaData.fieldType) {
      case FieldType.Color:
      case FieldType.Email:
      case FieldType.Link:
      case FieldType.PhoneNumber:
      case FieldType.LatLng:
      case FieldType.FieldID:
      case FieldType.LongToken:
        return true;
    }
    let fieldId = metaData.fieldId;
    if (!fieldId)
      return false;
    fieldId = fieldId.toLowerCase();
    return fieldId.includes("query") || fieldId.includes("classname") || fieldId.includes("parameter") || fieldId.includes("expression");
  }
}
